import { Injectable, inject } from '@angular/core';
import { map, Observable } from 'rxjs';

import { ApiClientService } from '../../../core/http/api-client.service';
import {
  CreateUserIntakeRequest,
  CreateUserRequest,
  ManualAdjustUserClientRatingRequest,
  SendUserInviteRequest,
  StaffRole,
  UpdateUserContactRequest,
  UpdateUserActiveRequest,
  UpdateUserRequest,
  UserDetailDto,
  UserDetailItem,
  UserDetailProcedureTypeDto,
  UserClientRatingAdjustment,
  UserClientRatingAdjustmentDto,
  UserClientRatingEvent,
  UserClientRatingEventDto,
  UserItem,
  UserItemDto,
  UserPageDto,
  UserPageItem
} from './users.models';

@Injectable({
  providedIn: 'root'
})
export class UsersService
{
  private readonly apiClient = inject(ApiClientService);
  private static readonly DEFAULT_ACCOUNT_STATUS: UserItem['accountStatus'] = 'registered';

  public getList(companyId?: string): Observable<UserItem[]>
  {
    return this.apiClient.get<UserItemDto[] | null>(
      '/users',
      companyId ? { companyId } : undefined,
    ).pipe(
      map((items: UserItemDto[] | null) =>
      {
        if (!Array.isArray(items))
        {
          return [];
        }

        return items.map((item) => ({
          ...this.mapUserDto(item),
        }));
      })
    );
  }

  public getPage(params: {
    companyId?: string;
    limit: number;
    cursor?: string | null;
    role?: StaffRole | 'company_admin' | 'system_admin' | '';
    status?: 'active' | 'inactive' | '';
    query?: string;
    sort?: 'createdAtDesc' | 'createdAtAsc';
  }): Observable<UserPageItem> {
    return this.apiClient.get<UserPageDto>(
      '/users',
      {
        ...(params.companyId ? { companyId: params.companyId } : {}),
        limit: String(params.limit),
        cursor: params.cursor ?? undefined,
        role: params.role || undefined,
        status: params.status || undefined,
        q: params.query?.trim() || undefined,
        sort: params.sort ?? 'createdAtDesc',
      },
    ).pipe(
      map((page) => ({
        items: Array.isArray(page?.items) ? page.items.map((item) => this.mapUserDto(item)) : [],
        nextCursor: page?.nextCursor ?? null,
        hasMore: !!page?.hasMore,
      })),
    );
  }

  public create(payload: CreateUserRequest, companyId?: string): Observable<UserItem>
  {
    return this.apiClient.post<UserItemDto, CreateUserRequest>(
      '/users',
      payload,
      companyId ? { companyId } : undefined,
    ).pipe(
      map((item: UserItemDto) => this.mapUserDto(item))
    );
  }

  public getById(userId: string, companyId?: string): Observable<UserItem>
  {
    return this.apiClient.get<UserItemDto>(
      `/users/${userId}`,
      companyId ? { companyId } : undefined,
    ).pipe(
      map((item: UserItemDto) => this.mapUserDto(item))
    );
  }

  public getDetailById(userId: string, companyId?: string): Observable<UserDetailItem>
  {
    return this.apiClient.get<UserDetailDto>(
      `/users/${encodeURIComponent(userId)}/detail`,
      companyId ? { companyId } : undefined,
    ).pipe(
      map((item: UserDetailDto) => ({
        ...this.mapUserDto(item),
        clinic: item.clinic
          ? {
              id: item.clinic.id,
              name: item.clinic.name,
              city: item.clinic.city,
              address: item.clinic.address,
            }
          : null,
        specialistProcedureTypes: Array.isArray(item.specialistProcedureTypes)
          ? item.specialistProcedureTypes.map((procedureType) =>
              this.mapDetailProcedureType(procedureType),
            )
          : [],
      })),
    );
  }

  public getClientRatingEvents(userId: string, companyId?: string): Observable<UserClientRatingEvent[]> {
    return this.apiClient.get<UserClientRatingEventDto[] | null>(
      `/users/client-rating-events/${userId}`,
      companyId ? { companyId } : undefined,
    ).pipe(
      map((items: UserClientRatingEventDto[] | null) =>
      {
        if (!Array.isArray(items)) {
          return [];
        }

        return items.map((item) => ({
          id: item.id,
          clientId: item.clientId,
          appointmentId: item.appointmentId ?? null,
          eventType: item.eventType,
          sourceType: item.sourceType,
          sourceId: item.sourceId,
          deltaPercent: item.deltaPercent,
          scoreBefore: item.scoreBefore,
          scoreAfter: item.scoreAfter,
          actorUserId: item.actorUserId ?? null,
          actorRole: item.actorRole ?? null,
          note: item.note ?? null,
          payloadJson: item.payloadJson ?? null,
          createdAt: item.createdAt,
        }));
      }),
    );
  }

  public manualAdjustClientRating(
    userId: string,
    payload: ManualAdjustUserClientRatingRequest,
    companyId?: string,
  ): Observable<UserClientRatingAdjustment> {
    return this.apiClient.post<UserClientRatingAdjustmentDto, ManualAdjustUserClientRatingRequest>(
      `/users/client-rating-adjustments/${userId}`,
      payload,
      companyId ? { companyId } : undefined,
    ).pipe(
      map((item) => ({
        currentScorePercent: item.currentScorePercent,
        event: {
          id: item.event.id,
          clientId: item.event.clientId,
          appointmentId: item.event.appointmentId ?? null,
          eventType: item.event.eventType,
          sourceType: item.event.sourceType,
          sourceId: item.event.sourceId,
          deltaPercent: item.event.deltaPercent,
          scoreBefore: item.event.scoreBefore,
          scoreAfter: item.event.scoreAfter,
          actorUserId: item.event.actorUserId ?? null,
          actorRole: item.event.actorRole ?? null,
          note: item.event.note ?? null,
          payloadJson: item.event.payloadJson ?? null,
          createdAt: item.event.createdAt,
        },
      })),
    );
  }

  public update(userId: string, payload: UpdateUserRequest, companyId?: string): Observable<UserItem>
  {
    return this.apiClient.patch<UserItemDto, UpdateUserRequest>(
      `/users/${userId}`,
      payload,
      companyId ? { companyId } : undefined,
    ).pipe(
      map((item: UserItemDto) => this.mapUserDto(item))
    );
  }

  public updateActive(
    userId: string,
    payload: UpdateUserActiveRequest,
    companyId?: string,
  ): Observable<UserItem>
  {
    return this.apiClient.patch<UserItemDto, UpdateUserActiveRequest>(
      `/users/${userId}/active`,
      payload,
      companyId ? { companyId } : undefined,
    ).pipe(
      map((item: UserItemDto) => this.mapUserDto(item))
    );
  }

  public createIntake(payload: CreateUserIntakeRequest, companyId?: string): Observable<UserItem>
  {
    return this.apiClient.post<UserItemDto, CreateUserIntakeRequest>(
      '/users/intake',
      payload,
      companyId ? { companyId } : undefined,
    ).pipe(
      map((item: UserItemDto) => this.mapUserDto(item))
    );
  }

  public updateContact(
    userId: string,
    payload: UpdateUserContactRequest,
    companyId?: string,
  ): Observable<UserItem>
  {
    return this.apiClient.patch<UserItemDto, UpdateUserContactRequest>(
      `/users/${userId}/contact`,
      payload,
      companyId ? { companyId } : undefined,
    ).pipe(
      map((item: UserItemDto) => this.mapUserDto(item))
    );
  }

  public sendInvite(
    userId: string,
    payload: SendUserInviteRequest,
    companyId?: string,
  ): Observable<{ inviteId: string; status: 'pending' | 'accepted' | 'expired' | 'revoked'; expiresAt: string }>
  {
    return this.apiClient.post<
      { inviteId: string; status: 'pending' | 'accepted' | 'expired' | 'revoked'; expiresAt: string },
      SendUserInviteRequest
    >(
      `/users/${userId}/invites`,
      payload,
      companyId ? { companyId } : undefined,
    );
  }

  public uploadPhoto(userId: string, file: File, companyId?: string): Observable<UserItem> {
    const body = new FormData();
    body.append('file', file);

    return this.apiClient.post<UserItemDto, FormData>(
      `/users/${userId}/photo`,
      body,
      companyId ? { companyId } : undefined,
    ).pipe(
      map((item: UserItemDto) => this.mapUserDto(item))
    );
  }

  public removePhoto(userId: string, companyId?: string): Observable<UserItem> {
    return this.apiClient.patch<UserItemDto, Record<string, never>>(
      `/users/${userId}/photo/remove`,
      {},
      companyId ? { companyId } : undefined,
    ).pipe(
      map((item: UserItemDto) => this.mapUserDto(item))
    );
  }

  private mapUserDto(item: UserItemDto): UserItem {
    return {
      id: item.id,
      sourceKind: 'user',
      linkedUserId: item.id,
      clinicId: item.clinicId ?? null,
      clinicName: item.clinicName ?? null,
      photoUrl: item.photoUrl ?? null,
      fullName: item.fullName,
      birthDate: item.birthDate ?? null,
      email: item.email ?? '',
      phone: item.phone ?? null,
      role: item.role,
      isActive: item.isActive ?? true,
      accountStatus: item.accountStatus ?? UsersService.DEFAULT_ACCOUNT_STATUS,
      inviteExpiresAt: item.inviteExpiresAt ?? null,
      accountOrigin: null,
      clientProfile: item.clientProfile ?? null,
      specialistProcedureTypeIds: item.specialistProcedureTypeIds ?? [],
      createdAt: item.createdAt ?? '',
      updatedAt: item.updatedAt ?? ''
    };
  }

  private mapDetailProcedureType(
    item: UserDetailProcedureTypeDto,
  ): UserDetailItem['specialistProcedureTypes'][number] {
    return {
      id: item.id,
      name: item.name,
      deviceTypeId: item.deviceTypeId,
      deviceTypeName: item.deviceTypeName,
      defaultDurationMinutes: item.defaultDurationMinutes,
      isActive: item.isActive,
    };
  }
}
